import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Handles add to cart for samples and recommendations in the cart drawer
 */
class CartSamplesRecommendations {
  constructor() {
    this.init();
  }

  init() {
    // Handle free sample Add buttons
    document.addEventListener('click', (event) => {
      const sampleAddButton = event.target.closest('.cart-drawer__free-sample-add');
      if (sampleAddButton && !sampleAddButton.disabled) {
        this.handleAddToCart(event, sampleAddButton, 'sample');
        return;
      }

      // Handle recommendation Add buttons
      const addButton = event.target.closest('.cart-drawer__recommendation-add');
      if (addButton && !addButton.disabled) {
        this.handleAddToCart(event, addButton, 'recommendation');
      }
    });

    // Setup disqualifying products check
    this.setupDisqualifyingProductCheck();
  }

  /**
   * Handle add to cart for samples and recommendations
   * @param {Event} event - The click event
   * @param {HTMLElement} button - The button that was clicked
   * @param {string} type - 'sample' or 'recommendation'
   */
  async handleAddToCart(event, button, type) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    event.preventDefault();

    // Disable button and show loading state
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding...';

    const cartItemsComponent = document.querySelector('cart-items-component');
    if (!cartItemsComponent) {
      button.disabled = false;
      button.textContent = originalText;
      return;
    }

    const sectionId = cartItemsComponent.dataset.sectionId;
    if (!sectionId) {
      button.disabled = false;
      button.textContent = originalText;
      return;
    }

    try {
      const fetchCfg = fetchConfig('javascript', {
        body: JSON.stringify({
          sections: sectionId,
          sections_url: window.location.pathname,
          items: [
            {
              id: parseInt(variantId),
              quantity: 1,
            },
          ],
        }),
      });

      fetchCfg.headers['X-Requested-With'] = 'XMLHttpRequest';
      fetchCfg.headers['Content-Type'] = 'application/json';
      fetchCfg.method = 'POST';

      const response = await fetch(Theme.routes.cart_add_url, fetchCfg);
      const data = await response.json();

      if (data.status) {
        console.error(`Error adding ${type} to cart:`, data.description);
        button.disabled = false;
        button.textContent = originalText;
        return;
      }

      // Re-render the cart section
      if (data.sections && data.sections[sectionId]) {
        await sectionRenderer(data.sections[sectionId], sectionId);
      }

      // Dispatch cart update event
      document.dispatchEvent(new CartUpdateEvent({ source: 'cart-samples-recommendations' }));

      // Check for disqualifying products after update
      setTimeout(() => {
        this.checkDisqualifyingProducts();
      }, 100);
    } catch (error) {
      console.error(`Error adding ${type} to cart:`, error);
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  /**
   * Check for disqualifying products and hide free sample panel if needed
   */
  checkDisqualifyingProducts() {
    const disqualifyingProductsScript = document.getElementById('free-sample-disqualifying-products');
    if (!disqualifyingProductsScript) return;

    try {
      const disqualifyingProductIds = JSON.parse(disqualifyingProductsScript.textContent);
      const freeSamplePanel = document.querySelector('[data-free-sample-panel]');
      if (!freeSamplePanel) return;

      // Get all cart items - check for data-product-id or other identifiers
      const cartItems = document.querySelectorAll('[data-product-id]');
      let hasDisqualifyingProduct = false;

      cartItems.forEach((item) => {
        const productId = item.dataset.productId ? parseInt(item.dataset.productId) : null;
        if (productId && disqualifyingProductIds.includes(productId)) {
          hasDisqualifyingProduct = true;
        }
      });

      // Also check cart item rows
      const cartItemRows = document.querySelectorAll('.cart-items__table-row[data-product-id]');
      cartItemRows.forEach((row) => {
        const productId = row.dataset.productId ? parseInt(row.dataset.productId) : null;
        if (productId && disqualifyingProductIds.includes(productId)) {
          hasDisqualifyingProduct = true;
        }
      });

      // Hide/show the free sample panel
      if (hasDisqualifyingProduct) {
        freeSamplePanel.style.display = 'none';
      } else {
        freeSamplePanel.style.display = '';
      }
    } catch (e) {
      console.error('Error checking disqualifying products:', e);
    }
  }

  /**
   * Setup hooks for cart drawer updates
   */
  setupDisqualifyingProductCheck() {
    this.checkDisqualifyingProducts();

    // Watch for DOM changes in cart drawer with MutationObserver
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer) return;

    const dialog = cartDrawer.querySelector('dialog');
    if (!dialog) return;

    // Remove old observer if exists
    if (dialog._disqualifyingObserver) {
      dialog._disqualifyingObserver.disconnect();
    }

    const observer = new MutationObserver(() => {
      // Debounce checks
      clearTimeout(dialog._checkTimeout);
      dialog._checkTimeout = setTimeout(() => {
        this.checkDisqualifyingProducts();
      }, 50);
    });

    observer.observe(dialog, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    dialog._disqualifyingObserver = observer;

    // Listen for cart update events
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      setTimeout(() => {
        this.checkDisqualifyingProducts();
      }, 100);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartSamplesRecommendations();
  });
} else {
  new CartSamplesRecommendations();
}
